import { Editor } from '@tiptap/react';
import { FC, useCallback, useState } from 'react';

import { Button, Spinning } from 'components';
import { TAB_CHAR } from 'constants/keyboard';
import {
  BoldWysiwygIcon,
  CenterWysiwygIcon,
  CheckCircleIcon,
  CopyLightIcon,
  DecreaseWysiwygIcon,
  DownloadIcon,
  HeaderOneWysiwygIcon,
  HeaderThreeWysiwygIcon,
  HeaderTwoWysiwygIcon,
  HyperlinkWysiwygIcon,
  ImageWysiwygIcon,
  IncreaseWysiwygIcon,
  ItalicWysiwygIcon,
  LeftWysiwygIcon,
  OrderedWysiwygIcon,
  RedoWysiwygIcon,
  RegenerationIcon,
  RightWysiwygIcon,
  UnderlineWysiwygIcon,
  UndoWysiwygIcon,
  UnorderedWysiwygIcon
} from 'lib/svgs/Icons';
import { useTranslations } from 'next-intl';
import { notifyException } from 'services/appsignal';
import { ESpansException } from 'types/ESpans';
import { cn } from 'utils/cn';
import { useContentGenerationContext } from './_context/ContentGenerationContext';

interface IMenuBar {
  editor: Editor | null;
}

const MenuBar: FC<IMenuBar> = ({ editor }) => {
  const t = useTranslations();

  const { handleRegenerate, isNotInitialized, generatedContent } = useContentGenerationContext();

  const [copying, setCopying] = useState(false);
  const [spinning, setSpining] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const onCopy = useCallback(() => {
    setCopying(true);
    navigator.clipboard.writeText(editor?.getText() ?? '');
    setTimeout(() => {
      setCopying(false);
    }, 1000);
  }, [editor, setCopying]);

  const handleDownload = useCallback(() => {
    setDownloading(true);
    editor
      ?.chain()
      ?.export({
        format: 'docx',
        onExport: (context) => {
          if (context?.error)
            notifyException(context?.error, ESpansException.ContentGenerationException);

          const element = document.createElement('a');
          const file = context?.blob as Blob;
          element.href = URL.createObjectURL(file);
          element.download = `${generatedContent?.title}.docx`;
          document.body.appendChild(element);
          element.click();
          setDownloading(false);
        }
      })
      ?.run();
  }, [editor, generatedContent]);

  const onRegenerate = useCallback(() => {
    handleRegenerate();
    setSpining(true);
    setTimeout(() => {
      setSpining(false);
    }, 1000);
  }, [handleRegenerate, setSpining]);

  const setLink = useCallback(() => {
    if (editor) {
      const previousUrl = editor.getAttributes('link').href;
      const url = window.prompt('URL', previousUrl);

      // cancelled
      if (url === null) {
        return;
      }

      // empty
      if (url === '') {
        editor.chain().focus().extendMarkRange('link').unsetLink().run();

        return;
      }

      // update link
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
  }, [editor]);

  const addImage = useCallback(() => {
    if (editor) {
      const url = window.prompt('URL');

      if (url) {
        editor.chain().focus().setImage({ src: url }).run();
      }
    }
  }, [editor]);

  if (!editor) return null;

  const indent = () => {
    const { selection } = editor.state;
    const { $from } = selection;

    // Check if we're at the start of a list item
    if (editor.isActive('listItem') && $from.parentOffset === 0) {
      // Attempt to sink the list item
      const sinkResult = editor.chain().sinkListItem('listItem').run();

      // If sinking was successful, return true
      if (sinkResult) {
        return true;
      }
      // If sinking failed, we'll fall through to inserting a tab
    }

    // Insert a tab character
    editor
      .chain()
      .command(({ tr }) => {
        tr.insertText(TAB_CHAR);
        return true;
      })
      .run();

    // Prevent default behavior (losing focus)
    return true;
  };

  const outdent = () => {
    const { selection, doc } = editor.state;
    const { $from } = selection;
    const pos = $from.pos;

    // Check if we're at the start of a list item
    if (editor.isActive('listItem') && $from.parentOffset === 0) {
      // If so, lift the list item
      return editor.chain().liftListItem('listItem').run();
    }

    // Check if the previous character is a tab
    if (doc.textBetween(pos - 1, pos) === TAB_CHAR) {
      // If so, delete it
      editor
        .chain()
        .command(({ tr }) => {
          tr.delete(pos - 1, pos);
          return true;
        })
        .run();
      return true;
    }

    // Prevent default behavior (losing focus)
    return true;
  };

  const isBold = editor.isActive('bold');
  const isItalic = editor.isActive('italic');
  const isUnderline = editor.isActive('underline');
  const isH1 = editor.isActive('heading', { level: 1 });
  const isH2 = editor.isActive('heading', { level: 2 });
  const isH3 = editor.isActive('heading', { level: 3 });
  const isLeft = editor.isActive({ textAlign: 'left' });
  const isCenter = editor.isActive({ textAlign: 'center' });
  const isRight = editor.isActive({ textAlign: 'right' });
  const isUnorderedBullets = editor.isActive('bulletList');
  const isOrderedBullets = editor.isActive('orderedList');
  const isHiperlink = editor.isActive('link');

  return (
    <div className="flex justify-between">
      <div className="flex gap-2 ">
        <div data-tooltip-id="tolq-tooltip" data-tooltip-content={t('tooltips.undo')}>
          <Button
            size="small"
            variant="icon-light"
            color="inherit"
            className={cn(
              '!m-0 !rounded-3xl',
              isBold ? '!bg-primary-blue-900' : 'hover:!bg-tertiary-800'
            )}
            onClick={() => editor.chain().focus().undo().run()}
            startAdornment={<UndoWysiwygIcon className={cn('cursor-pointer !text-tertiary-200')} />}
          />
        </div>
        <div data-tooltip-id="tolq-tooltip" data-tooltip-content={t('tooltips.redo')}>
          <Button
            size="small"
            variant="icon-light"
            color="inherit"
            className={cn(
              '!m-0 !rounded-3xl',
              isBold ? '!bg-primary-blue-900' : 'hover:!bg-tertiary-800'
            )}
            onClick={() => editor.chain().focus().redo().run()}
            startAdornment={<RedoWysiwygIcon className={cn('cursor-pointer !text-tertiary-200')} />}
          />
        </div>
        <div className="border-0 border-r-1 border-tertiary-800" />
        <div data-tooltip-id="tolq-tooltip" data-tooltip-content={t('tooltips.bold')}>
          <Button
            size="small"
            variant="icon-light"
            color="inherit"
            className={cn(
              '!m-0 !rounded-3xl',
              isBold ? '!bg-primary-blue-900' : 'hover:!bg-tertiary-800'
            )}
            onClick={() => editor.chain().focus().toggleBold().run()}
            startAdornment={
              <BoldWysiwygIcon
                className={cn(
                  isBold ? '!text-secondary-blue' : '!text-tertiary-200',
                  'cursor-pointer'
                )}
              />
            }
          />
        </div>
        <div data-tooltip-id="tolq-tooltip" data-tooltip-content={t('tooltips.italic')}>
          <Button
            size="small"
            variant="icon-light"
            color="inherit"
            className={cn(
              '!m-0 !rounded-3xl',
              isItalic ? '!bg-primary-blue-900' : 'hover:!bg-tertiary-800'
            )}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            startAdornment={
              <ItalicWysiwygIcon
                className={cn(
                  isItalic ? '!text-secondary-blue' : '!text-tertiary-200',
                  'cursor-pointer'
                )}
              />
            }
          />
        </div>
        <div data-tooltip-id="tolq-tooltip" data-tooltip-content={t('tooltips.underline')}>
          <Button
            size="small"
            variant="icon-light"
            color="inherit"
            className={cn(
              '!m-0 !rounded-3xl',
              isUnderline ? '!bg-primary-blue-900' : 'hover:!bg-tertiary-800'
            )}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            startAdornment={
              <UnderlineWysiwygIcon
                className={cn(
                  isUnderline ? '!text-secondary-blue' : '!text-tertiary-200',
                  'cursor-pointer'
                )}
              />
            }
          />
        </div>
        <div className="border-0 border-r-1 border-tertiary-800" />
        <div data-tooltip-id="tolq-tooltip" data-tooltip-content={t('tooltips.heading_one')}>
          <Button
            size="small"
            variant="icon-light"
            color="inherit"
            className={cn(
              '!m-0 !rounded-3xl',
              isH1 ? '!bg-primary-blue-900' : 'hover:!bg-tertiary-800'
            )}
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            startAdornment={
              <HeaderOneWysiwygIcon
                className={cn(
                  isH1 ? '!text-secondary-blue' : '!text-tertiary-200',
                  'cursor-pointer'
                )}
              />
            }
          />
        </div>
        <div data-tooltip-id="tolq-tooltip" data-tooltip-content={t('tooltips.heading_two')}>
          <Button
            size="small"
            variant="icon-light"
            color="inherit"
            className={cn(
              '!m-0 !rounded-3xl',
              isH2 ? '!bg-primary-blue-900' : 'hover:!bg-tertiary-800'
            )}
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            startAdornment={
              <HeaderTwoWysiwygIcon
                className={cn(
                  isH2 ? '!text-secondary-blue' : '!text-tertiary-200',
                  'cursor-pointer'
                )}
              />
            }
          />
        </div>
        <div data-tooltip-id="tolq-tooltip" data-tooltip-content={t('tooltips.heading_three')}>
          <Button
            size="small"
            variant="icon-light"
            color="inherit"
            className={cn(
              '!m-0 !rounded-3xl',
              isH3 ? '!bg-primary-blue-900' : 'hover:!bg-tertiary-800'
            )}
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            startAdornment={
              <HeaderThreeWysiwygIcon
                className={cn(
                  isH3 ? '!text-secondary-blue' : '!text-tertiary-200',
                  'cursor-pointer'
                )}
              />
            }
          />
        </div>
        <div className="border-0 border-r-1 border-tertiary-800" />
        <div data-tooltip-id="tolq-tooltip" data-tooltip-content={t('tooltips.left_align')}>
          <Button
            size="small"
            variant="icon-light"
            color="inherit"
            className={cn(
              '!m-0 !rounded-3xl',
              isLeft ? '!bg-primary-blue-900' : 'hover:!bg-tertiary-800'
            )}
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
            startAdornment={
              <LeftWysiwygIcon
                className={cn(
                  isLeft ? '!text-secondary-blue' : '!text-tertiary-200',
                  'cursor-pointer'
                )}
              />
            }
          />
        </div>
        <div data-tooltip-id="tolq-tooltip" data-tooltip-content={t('tooltips.center_align')}>
          <Button
            size="small"
            variant="icon-light"
            color="inherit"
            className={cn(
              '!m-0 !rounded-3xl',
              isCenter ? '!bg-primary-blue-900' : 'hover:!bg-tertiary-800'
            )}
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
            startAdornment={
              <CenterWysiwygIcon
                className={cn(
                  isCenter ? '!text-secondary-blue' : '!text-tertiary-200',
                  'cursor-pointer'
                )}
              />
            }
          />
        </div>
        <div data-tooltip-id="tolq-tooltip" data-tooltip-content={t('tooltips.right_align')}>
          <Button
            size="small"
            variant="icon-light"
            color="inherit"
            className={cn(
              '!m-0 !rounded-3xl',
              isRight ? '!bg-primary-blue-900' : 'hover:!bg-tertiary-800'
            )}
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
            startAdornment={
              <RightWysiwygIcon
                className={cn(
                  isRight ? '!text-secondary-blue' : '!text-tertiary-200',
                  'cursor-pointer'
                )}
              />
            }
          />
        </div>
        <div className="border-0 border-r-1 border-tertiary-800" />
        <div data-tooltip-id="tolq-tooltip" data-tooltip-content={t('tooltips.numbered_list')}>
          <Button
            size="small"
            variant="icon-light"
            color="inherit"
            className={cn(
              '!m-0 !rounded-3xl',
              isUnorderedBullets ? '!bg-primary-blue-900' : 'hover:!bg-tertiary-800'
            )}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            startAdornment={
              <UnorderedWysiwygIcon
                className={cn(
                  isUnorderedBullets ? '!text-secondary-blue' : '!text-tertiary-200',
                  'cursor-pointer'
                )}
              />
            }
          />
        </div>
        <div data-tooltip-id="tolq-tooltip" data-tooltip-content={t('tooltips.bullet_list')}>
          <Button
            size="small"
            variant="icon-light"
            color="inherit"
            className={cn(
              '!m-0 !rounded-3xl',
              isOrderedBullets ? '!bg-primary-blue-900' : 'hover:!bg-tertiary-800'
            )}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            startAdornment={
              <OrderedWysiwygIcon
                className={cn(
                  isOrderedBullets ? '!text-secondary-blue' : '!text-tertiary-200',
                  'cursor-pointer'
                )}
              />
            }
          />
        </div>
        <div className="border-0 border-r-1 border-tertiary-800" />
        <div data-tooltip-id="tolq-tooltip" data-tooltip-content={t('tooltips.outdent')}>
          <Button
            size="small"
            variant="icon-light"
            color="inherit"
            className="!m-0 !rounded-3xl"
            onClick={outdent}
            startAdornment={<DecreaseWysiwygIcon className="cursor-pointer" />}
          />
        </div>
        <div data-tooltip-id="tolq-tooltip" data-tooltip-content={t('tooltips.indent')}>
          <Button
            size="small"
            variant="icon-light"
            color="inherit"
            className="!m-0 !rounded-3xl"
            onClick={indent}
            startAdornment={<IncreaseWysiwygIcon className="cursor-pointer" />}
          />
        </div>
        <div className="border-0 border-r-1 border-tertiary-800" />
        <div data-tooltip-id="tolq-tooltip" data-tooltip-content={t('tooltips.insert_link')}>
          <Button
            size="small"
            variant="icon-light"
            color="inherit"
            className={cn(
              '!m-0 !rounded-3xl',
              isHiperlink ? '!bg-primary-blue-900' : 'hover:!bg-tertiary-800'
            )}
            onClick={() => (isHiperlink ? editor.chain().focus().unsetLink().run() : setLink())}
            startAdornment={
              <HyperlinkWysiwygIcon
                className={cn(
                  isHiperlink ? '!text-secondary-blue' : '!text-tertiary-200',
                  'cursor-pointer'
                )}
              />
            }
          />
        </div>
        {false && (
          <div data-tooltip-id="tolq-tooltip" data-tooltip-content={t('tooltips.insert_image')}>
            <Button
              size="small"
              variant="icon-light"
              color="inherit"
              className={cn(
                '!m-0 !rounded-3xl',
                isBold ? '!bg-primary-blue-900' : 'hover:!bg-tertiary-800'
              )}
              onClick={addImage}
              startAdornment={<ImageWysiwygIcon />}
              disabled
            />
          </div>
        )}
      </div>
      <div className="flex gap-2">
        {false && (
          <div data-tooltip-id="tolq-tooltip" data-tooltip-content={t('tooltips.reload')}>
            <Button
              size="small"
              variant="icon-light"
              color="inherit"
              className="!m-0 !w-8 !rounded-3xl hover:!bg-tertiary-800"
              onClick={onRegenerate}
              startAdornment={
                <RegenerationIcon
                  className={cn(spinning ? 'animate-spin' : '', '!text-tertiary-200')}
                />
              }
              disabled={isNotInitialized || true}
            />
          </div>
        )}
        <div data-tooltip-id="tolq-tooltip" data-tooltip-content={t('tooltips.copy')}>
          <Button
            size="small"
            variant="icon-light"
            color="inherit"
            className="!m-0 !w-8 !rounded-3xl hover:!bg-tertiary-800"
            onClick={onCopy}
            startAdornment={
              copying ? (
                <CheckCircleIcon className="!text-tertiary-200" />
              ) : (
                <CopyLightIcon className="!text-tertiary-200" />
              )
            }
          />
        </div>
        <div data-tooltip-id="tolq-tooltip" data-tooltip-content={t('tooltips.download')}>
          <Button
            size="small"
            variant="icon-light"
            color="inherit"
            className={'!m-0 !w-8 !rounded-3xl hover:!bg-tertiary-800 '}
            onClick={handleDownload}
            startAdornment={
              downloading ? (
                <div className="relative mb-6 mt-0">
                  <Spinning className={cn('top-[2px]')} />
                </div>
              ) : (
                <DownloadIcon className="scale-150 !text-tertiary-200" />
              )
            }
          />
        </div>
      </div>
    </div>
  );
};

export default MenuBar;
